import { Head, Link, router } from '@inertiajs/react'
import { Badge, Box, Button, Callout, Card, Flex, Grid, Heading, Kbd, Section, Text } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import { CheckCircle2, Instagram } from 'lucide-react'
import ManagerPageHeader from '@/shared/manager/ManagerPageHeader'
import ColorSwatch from '@/shared/cars/ColorSwatch'
import {
  TrackManagerCarReviewsShowCar as Car,
  TrackManagerCarReviewsShowProps,
  TrackManagerCarReviewsShowReason as Reason,
} from '@/types/track_manager_car_reviews'

const FIELD_LABELS: Record<string, string> = {
  driver_name: 'Driver',
  year: 'Year',
  make: 'Make',
  model: 'Model',
  trim: 'Trim',
  chassis_code: 'Chassis',
  engine: 'Engine',
  charger: 'Charger',
  body_kit: 'Body kit',
  wheels: 'Wheels',
  instagram: 'Instagram',
}

const BASE = '/track_manager/car_reviews'

export default function CarReviewsShow({ car, waiting_count, reported_count, upcoming_photo_urls, reasons, last_decision }: TrackManagerCarReviewsShowProps) {
  // Warm the next cars' photos while this one is being looked at, so the next
  // car is on screen the moment this one is decided.
  useEffect(() => {
    upcoming_photo_urls.forEach((url) => {
      const img = new Image()
      img.src = url
    })
  }, [upcoming_photo_urls])

  return (
    <div className="flex">
      <Head title="Car reviews" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Car reviews">
          <Button variant="soft" color="gray" asChild>
            <Link href={`${BASE}/history`}>History</Link>
          </Button>
        </ManagerPageHeader>
        <Section size="1">
          <Box px={{ initial: '3', sm: '0' }}>
            {last_decision && <UndoBar decision={last_decision} />}
            <Text as="p" color="gray" mb="3">
              {waiting_count} waiting
              {reported_count > 0 && <Text color="red"> · {reported_count} reported</Text>}
            </Text>
            {car ? (
              <Reviewer key={car.id} car={car} reasons={reasons} canUndo={last_decision !== null} />
            ) : (
              <Flex direction="column" align="center" gap="2" py="9">
                <CheckCircle2 size={40} color="var(--green-9)" />
                <Heading size="5">Queue's clear.</Heading>
              </Flex>
            )}
          </Box>
        </Section>
      </div>
    </div>
  )
}

function UndoBar({ decision }: { decision: NonNullable<TrackManagerCarReviewsShowProps['last_decision']> }) {
  const verb = decision.decision === 'approved' ? 'Approved' : 'Rejected'
  return (
    <Callout.Root color="gray" mb="3">
      <Flex justify="between" align="center" gap="3" width="100%">
        <Callout.Text>
          {verb} {decision.car_label}
          {decision.reason_label && ` · ${decision.reason_label}`}
        </Callout.Text>
        <Button size="1" variant="soft" onClick={() => router.patch(`${BASE}/undo`)}>
          Undo <Kbd>U</Kbd>
        </Button>
      </Flex>
    </Callout.Root>
  )
}

function Reviewer({ car, reasons, canUndo }: { car: Car; reasons: Reason[]; canUndo: boolean }) {
  const [picking, setPicking] = useState(false)
  const [reason, setReason] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const send = (method: 'patch' | 'post', url: string, data: Record<string, string> = {}) => {
    if (busy) return
    setBusy(true)
    router[method](url, data, { onFinish: () => setBusy(false) })
  }

  const approve = () => send('patch', `${BASE}/${car.id}`, { decision: 'approve', expected_status: car.review_status })
  const reject = () => {
    if (!reason) return
    send('patch', `${BASE}/${car.id}`, { decision: 'reject', reason, expected_status: car.review_status })
  }
  const skip = () => send('post', `${BASE}/${car.id}/skip`)
  const undo = () => send('patch', `${BASE}/undo`)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement
      if (target.closest('input, textarea, select, [contenteditable]')) return

      const key = e.key.toLowerCase()
      if (picking) {
        const n = parseInt(key, 10)
        if (n >= 1 && n <= reasons.length) setReason(reasons[n - 1].value)
        else if (key === 'enter') reject()
        else if (key === 'escape') setPicking(false)
        else return
      } else if (key === 'a') approve()
      else if (key === 'r') setPicking(true)
      else if (key === 's' || key === 'arrowright') skip()
      else if (key === 'u' && canUndo) undo()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const changed = new Set(car.changed_fields ?? [])

  return (
    <Grid columns={{ initial: '1', md: '3fr 1fr' }} gap="5">
      <Box>
        {car.photos.length === 0 ? (
          <Flex align="center" justify="center" style={{ height: 320, background: 'var(--gray-3)' }}>
            <Text color="gray">No photos</Text>
          </Flex>
        ) : (
          <Grid columns={{ initial: '1', sm: String(car.photos.length) }} gap="3">
            {car.photos.map((photo) => (
              <Box key={photo.id} position="relative">
                <img
                  src={photo.url ?? undefined}
                  alt="Car photo under review"
                  style={{ display: 'block', width: '100%', height: '60vh', objectFit: 'contain', background: 'var(--gray-3)' }}
                />
                {photo.is_new && (
                  <Badge color="amber" variant="solid" style={{ position: 'absolute', top: 8, left: 8 }}>New</Badge>
                )}
              </Box>
            ))}
          </Grid>
        )}
      </Box>

      <Flex direction="column" gap="4">
        <Box>
          <Flex align="center" gap="2">
            {car.color_family && <ColorSwatch color={car.color_family} size={20} />}
            <Heading size="5">
              {car.color_family && `${car.color_family.label} `}
              {car.heading}
            </Heading>
          </Flex>
          {changed.has('color_family') && <Badge color="amber" mt="1">Color changed</Badge>}
          <Flex gap="2" mt="2" wrap="wrap">
            {car.review_status !== 'pending' && (
              <Badge color={car.review_status === 'approved' ? 'green' : 'red'}>
                Currently {car.review_status}
                {car.rejection_reason_label && `: ${car.rejection_reason_label}`}
              </Badge>
            )}
            {car.reports.length > 0 ? (
              <Badge color="red" variant="solid">Reported</Badge>
            ) : car.changed_fields === null ? (
              <Badge color="gray">First review</Badge>
            ) : (car.changed_fields.length > 0 || car.photos.some((p) => p.is_new)) && (
              <Badge color="amber">Resubmitted</Badge>
            )}
          </Flex>
        </Box>

        {car.reports.length > 0 && <ReportsPanel reports={car.reports} />}

        <Card>
          <Flex direction="column" gap="2">
            {car.fields.map((field) => (
              <Flex
                key={field.key}
                justify="between"
                gap="3"
                px="1"
                style={changed.has(field.key) ? { background: 'var(--amber-a4)', borderRadius: 4 } : undefined}
              >
                <Text size="2" color="gray">{FIELD_LABELS[field.key] ?? field.key}</Text>
                {field.key === 'instagram' && car.instagram ? (
                  <a href={`https://instagram.com/${car.instagram}`} target="_blank" rel="noreferrer">
                    <Flex align="center" gap="1">
                      <Instagram size={14} />
                      <Text size="2">@{car.instagram}</Text>
                    </Flex>
                  </a>
                ) : (
                  <Text size="2" weight="medium" align="right">{field.value || '—'}</Text>
                )}
              </Flex>
            ))}
          </Flex>
        </Card>

        <Box>
          <Text as="p" size="1" color="gray">Owner: {car.owner.email}</Text>
          <Text as="p" size="1" color="gray">
            Their other cars: {car.owner.other_approved} approved, {car.owner.other_rejected} rejected
          </Text>
          {car.submitted_at && <Text as="p" size="1" color="gray">Submitted {car.submitted_at}</Text>}
        </Box>

        {picking ? (
          <Card>
            <Text as="p" size="2" weight="bold" mb="2">Why? <Kbd>1</Kbd>–<Kbd>{reasons.length}</Kbd></Text>
            <Flex direction="column" gap="1">
              {reasons.map((r, i) => (
                <Button
                  key={r.value}
                  variant={reason === r.value ? 'solid' : 'soft'}
                  color={reason === r.value ? 'red' : 'gray'}
                  onClick={() => setReason(r.value)}
                  style={{ justifyContent: 'flex-start', height: 'auto', padding: '6px 10px', textAlign: 'left' }}
                >
                  <Kbd>{i + 1}</Kbd> {r.label}
                </Button>
              ))}
            </Flex>
            <Flex gap="2" mt="3" justify="end">
              <Button variant="soft" color="gray" onClick={() => setPicking(false)}>
                Cancel <Kbd>Esc</Kbd>
              </Button>
              <Button color="red" disabled={!reason || busy} onClick={reject}>
                Reject <Kbd>Enter</Kbd>
              </Button>
            </Flex>
          </Card>
        ) : (
          <Flex direction="column" gap="2">
            <Button size="3" color="green" disabled={busy} onClick={approve}>
              Approve <Kbd>A</Kbd>
            </Button>
            <Button size="3" color="red" variant="soft" disabled={busy} onClick={() => setPicking(true)}>
              Reject <Kbd>R</Kbd>
            </Button>
            <Button size="2" color="gray" variant="ghost" disabled={busy} onClick={skip}>
              Skip <Kbd>S</Kbd>
            </Button>
          </Flex>
        )}
      </Flex>
    </Grid>
  )
}

/**
 * Why a car is here, when someone reported it: each reason, how many people
 * picked it, and who they are — with their record, so a serial reporter shows.
 */
function ReportsPanel({ reports }: { reports: Car['reports'] }) {
  const counted = reports.filter((r) => r.counted)
  const tally = new Map<string, number>()
  counted.forEach((r) => tally.set(r.reason_label, (tally.get(r.reason_label) ?? 0) + 1))

  return (
    <Card style={{ borderColor: 'var(--red-7)' }}>
      <Flex direction="column" gap="2">
        <Text size="2" weight="bold" color="red">
          {[...tally.entries()].map(([label, n]) => (n > 1 ? `${label} ×${n}` : label)).join(', ') ||
            'Reported (all over the limit)'}
        </Text>
        {reports.map((report) => (
          <Flex key={report.id} direction="column" style={report.counted ? undefined : { opacity: 0.5 }}>
            <Text size="1">
              {report.reporter.email} · {report.reason_label}
              {!report.counted && ' · over the limit'}
            </Text>
            <Text size="1" color="gray">
              {report.reported_at} · has reported {report.reporter.filed}, {report.reporter.dismissed} dismissed,{' '}
              {report.reporter.upheld} upheld
            </Text>
          </Flex>
        ))}
      </Flex>
    </Card>
  )
}
